<template>
  <div class="check-history-page">
    <UserAppChrome back-mode="history" />

    <main class="check-history-shell user-checks-body">
        <div v-if="pageLoading" class="history-state" role="status" aria-live="polite">
          <span class="history-state__spinner" aria-hidden="true"></span>
          <strong>Caricamento dello storico</strong>
          <p>Stiamo recuperando i tuoi check e le comparazioni.</p>
        </div>

        <div v-else-if="!userId" class="history-state">
          <span class="material-icons" aria-hidden="true">lock</span>
          <strong>Accesso richiesto</strong>
          <p>Accedi per consultare lo storico dei tuoi check.</p>
        </div>

        <template v-else>
          <UserSectionHeader
            eyebrow="I tuoi progressi"
            title="Storico check"
            :description="hideChecksFromUser
              ? 'Lo storico dei check non è visibile per il tuo profilo.'
              : 'Confronta questionari, fotografie e misure per seguire con chiarezza l’evoluzione del tuo percorso.'"
          />

          <template v-if="!hideChecksFromUser">
            <div class="section-header section-header--outside">
              <div>
                <p class="section-label">Confronti</p>
                <h2>Comparazioni questionari</h2>
              </div>
              <small class="muted">Basate sui questionari iniziali e sui check caricati</small>
            </div>

            <section class="ucs-section section-card">
              <div v-if="comparisonsLoading">
                <p>Carico le comparazioni…</p>
              </div>
              <div v-else-if="comparisonsError">
                <p class="error-text">Errore: {{ comparisonsError }}</p>
              </div>
              <div v-else-if="!comparisonChecks.length">
                <p>Nessuna comparazione disponibile.</p>
              </div>
              <div v-else class="comparison-list">
                <div v-for="check in comparisonChecks" :key="`cmp_${check.id}`" class="comparison-card">
                  <div class="comparison-head">
                    <div>
                      <h3>Check {{ check.id.replace('questionario_', '') }}</h3>
                      <p class="muted">Programma: {{ check.percorsoId || '—' }}</p>
                    </div>
                    <div class="comparison-meta">
                      <span>{{ formatDateTime(check.createdAt) || '—' }}</span>
                      <span v-if="check.stepIndex != null">Step #{{ check.stepIndex + 1 }}</span>
                    </div>
                  </div>

                  <div v-if="hasPhotoComparison(check)" class="comparison-photos">
                    <div
                      v-for="photo in photoComparisonsFor(check)"
                      :key="photo.key || photo.label"
                      class="photo-comparison"
                    >
                      <div class="photo-comparison__head">
                        <h4>{{ photo.label }}</h4>
                      </div>

                      <div class="photo-columns" role="group" aria-label="Confronto foto">
                        <div class="photo-col photo-col--initial">
                          <div class="photo-col-title">Questionario iniziale</div>
                          <div v-if="photo.initialUrls.length" class="photo-roll">
                            <img
                              v-for="url in photo.initialUrls"
                              :key="url"
                              class="photo-col-img"
                              :src="url"
                              :alt="`${photo.label} questionario iniziale`"
                              loading="lazy"
                              @click="openLightbox(url)"
                            />
                          </div>
                          <div v-else class="photo-empty">Nessuna foto disponibile</div>
                        </div>
                        <div class="photo-col photo-col--check">
                          <div class="photo-col-title">Check</div>
                          <div v-if="photo.checkUrls.length" class="photo-roll">
                            <img
                              v-for="url in photo.checkUrls"
                              :key="url"
                              class="photo-col-img"
                              :src="url"
                              :alt="`${photo.label} check`"
                              loading="lazy"
                              @click="openLightbox(url)"
                            />
                          </div>
                          <div v-else class="photo-empty">—</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="comparison-placeholder">
                    <p>Nessuna foto caricata in questo check.</p>
                  </div>

                  <div v-if="hasMeasurements(check)" class="comparison-measurements">
                    <button
                      class="measurement-toggle"
                      type="button"
                      :aria-expanded="isMeasurementsOpen(check.id)"
                      @click="toggleMeasurements(check.id)"
                    >
                      <div>
                        <p class="measurement-toggle__title">Confronto misure</p>
                        <small class="measurement-toggle__hint">Tap per mostrare i dettagli delle misurazioni</small>
                      </div>
                      <span
                        class="material-icons measurement-toggle__icon"
                        :class="{ 'measurement-toggle__icon--open': isMeasurementsOpen(check.id) }"
                      >
                        expand_more
                      </span>
                    </button>
                    <transition name="ucs-accordion">
                      <div
                        v-show="isMeasurementsOpen(check.id)"
                        class="measurements-grid"
                      >
                        <article
                          v-for="(row, idx) in measurementComparisonsFor(check)"
                          :key="`meas_${idx}`"
                          class="measurement-card"
                        >
                          <p class="measurement-label">{{ row.label }}</p>
                          <div class="measurement-values">
                            <div class="measurement-pill measurement-pill--initial">
                              <span class="pill-label">Questionario iniziale</span>
                              <span class="pill-value">{{ formatMeasurement(row.initialValue) }}</span>
                            </div>
                            <div class="measurement-pill measurement-pill--check">
                              <span class="pill-label">Check</span>
                              <span class="pill-value">{{ formatMeasurement(row.checkValue) }}</span>
                            </div>
                          </div>
                        </article>
                      </div>
                    </transition>
                  </div>
                  <div v-else class="comparison-placeholder">
                    <p>Nessuna misurazione salvata per questa comparazione.</p>
                  </div>
                </div>
              </div>
            </section>

            <section class="ucs-section section-card">
              <div class="section-header">
                <div>
                  <p class="section-label">Archivio</p>
                  <h2>Check manuali</h2>
                </div>
                <small class="muted">Foto, note e misure caricate dagli specialisti</small>
              </div>
              <div v-if="checksLoading">
                <p>Caricamento check…</p>
              </div>
              <div v-else-if="checksError">
                <p class="error-text">Errore: {{ checksError }}</p>
              </div>
              <div v-else-if="!manualHistoryChecks.length">
                <p>Nessun check salvato.</p>
              </div>
              <div v-else class="history-list">
                <article v-for="check in manualHistoryChecks" :key="check.id" class="check-card">
                  <header>
                    <h3>Check del {{ formatDate(check.createdAt) }}</h3>
                    <p class="muted">Aggiornato {{ formatDateTime(check.updatedAt) }}</p>
                  </header>
                  <p class="check-text" v-if="check.text">{{ check.text }}</p>

                  <div v-if="visibleManualPhotoPairs(check).length" class="comparison-photos">
                    <div
                      v-for="pair in visibleManualPhotoPairs(check)"
                      :key="`${check.id}_${pair.key}`"
                      class="photo-comparison"
                    >
                      <div class="photo-comparison__head">
                        <h4>{{ pair.label }}</h4>
                      </div>
                      <div class="photo-columns" role="group" aria-label="Foto check manuale">
                        <div
                          v-for="slot in pair.slots"
                          :key="`${check.id}_${pair.key}_${slot.field}`"
                          class="photo-col photo-col--check"
                        >
                          <div class="photo-col-title">{{ slot.label }}</div>
                          <div v-if="check[slot.field]" class="photo-roll">
                            <img
                              class="photo-col-img"
                              :src="check[slot.field]"
                              :alt="`${pair.label} ${slot.label}`"
                              loading="lazy"
                              @click="openLightbox(check[slot.field])"
                            />
                          </div>
                          <div v-else class="photo-empty">—</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="tables-container" v-if="(check.measurementsOne && check.measurementsOne.length) || (check.measurementsTwo && check.measurementsTwo.length)">
                    <div v-if="check.measurementsOne && check.measurementsOne.length">
                      <h4>Misure 1</h4>
                      <div class="table-wrapper table-wrapper--manual">
                        <table>
                          <thead>
                            <tr>
                              <th>Caratteristica</th>
                              <th>Valore</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(m, i) in check.measurementsOne" :key="`m1_${i}`">
                              <td>{{ m.caratteristica }}</td>
                              <td>{{ m.valore }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-if="check.measurementsTwo && check.measurementsTwo.length">
                      <h4>Misure 2</h4>
                      <div class="table-wrapper table-wrapper--manual">
                        <table>
                          <thead>
                            <tr>
                              <th>Caratteristica</th>
                              <th>Valore</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(m, i) in check.measurementsTwo" :key="`m2_${i}`">
                              <td>{{ m.caratteristica }}</td>
                              <td>{{ m.valore }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </section>
          </template>
          <section v-else class="ucs-section section-card">
            <p class="muted">Lo storico e le comparazioni non sono visibili per il tuo profilo.</p>
          </section>

          <button class="ticket-button" type="button" @click="goToTicketPage">
            <span class="material-icons" aria-hidden="true">support_agent</span>
            <span>Scrivi al team tecnico</span>
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </button>
        </template>
    </main>

    <div v-if="selectedImage" class="lightbox" @click.self="closeLightbox">
      <button type="button" class="close" aria-label="Chiudi immagine" @click="closeLightbox">&times;</button>
      <img class="lightbox-content" :src="selectedImage" alt="Immagine ingrandita" />
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import UserAppChrome from './UserAppChrome.vue';
import UserSectionHeader from './UserSectionHeader.vue';

const INITIAL_MEASUREMENT_FIELDS = [
  { key: 'peso', label: 'Peso (kg)', aliases: ['peso_attuale', 'peso_check'] },
  { key: 'altezza', label: 'Altezza (cm)' },
  { key: 'fianchi', label: 'Fianchi (cm)', aliases: ['circonferenza_fianchi', 'fianchi_cm'] },
  { key: 'coscia', label: 'Coscia (cm)' },
  { key: 'addome', label: 'Addome (cm)', aliases: ['circonferenza_vita', 'vita'] },
  { key: 'torace', label: 'Torace (cm)' },
  { key: 'braccio', label: 'Braccio (cm)' },
  { key: 'massa_magra', label: 'Massa magra (%)', aliases: ['massaMagra'] },
  { key: 'massa_grassa', label: 'Massa grassa (%)', aliases: ['massaGrassa'] },
  { key: 'acqua_corporea', label: 'Acqua corporea (%)', aliases: ['acquaCorporea'] }
];

const CHECK_MEASUREMENT_FIELDS = [
  { key: 'peso_attuale', label: 'Peso (kg)', aliases: ['peso'] },
  { key: 'altezza', label: 'Altezza (cm)' },
  { key: 'circonferenza_vita', label: 'Vita (cm)', aliases: ['vita', 'addome'] },
  { key: 'circonferenza_fianchi', label: 'Fianchi (cm)', aliases: ['fianchi'] },
  { key: 'coscia', label: 'Coscia (cm)' },
  { key: 'addome', label: 'Addome (cm)', aliases: ['vita'] },
  { key: 'torace', label: 'Torace (cm)' },
  { key: 'braccio', label: 'Braccio (cm)' },
  { key: 'massa_magra', label: 'Massa magra (%)', aliases: ['massaMagra'] },
  { key: 'massa_grassa', label: 'Massa grassa (%)', aliases: ['massaGrassa'] },
  { key: 'acqua_corporea', label: 'Acqua corporea (%)', aliases: ['acquaCorporea'] }
];

const hasBaselineData = (doc) => {
  if (!doc) return false;
  const answers = doc.answers || {};
  const foto = answers.foto || {};
  const hasPhotos = Object.values(foto).some((entry) => {
    if (!entry) return false;
    if (typeof entry === 'string') return entry.trim().length > 0;
    if (typeof entry === 'object') {
      return !!(entry.path || entry.url || entry.name);
    }
    return false;
  });
  const hasMeasurements = INITIAL_MEASUREMENT_FIELDS.some((field) => {
    const keys = [field.key, ...(field.aliases || [])];
    return keys.some((key) => {
      const value = valueFromAnswer(answers, key);
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (typeof value === 'number') return Number.isFinite(value);
      return true;
    });
  });
  return hasPhotos || hasMeasurements;
};

const questionnaireGroupKey = (row) => {
  if (!row || typeof row !== 'object') return `__${Math.random().toString(36).slice(2)}`;
  return (
    row.sequenceGroupId ||
    row.percorsoId ||
    `__${row.id || Math.random().toString(36).slice(2)}`
  );
};

const PHOTO_FIELD_MAP = [
  {
    key: 'foto_frontale',
    label: 'Foto frontale',
    answerKeys: ['foto_frontale'],
    answerPaths: [
      ['foto_frontale'],
      ['foto', 'anteriore'],
      ['foto', 'frontale']
    ],
    initialKeys: ['foto_frontale'],
    initialPaths: [
      ['foto_frontale'],
      ['foto', 'anteriore'],
      ['foto', 'frontale']
    ],
    isPrimary: true
  },
  {
    key: 'foto_posteriore',
    label: 'Foto posteriore',
    answerKeys: ['foto_posteriore'],
    answerPaths: [
      ['foto_posteriore'],
      ['foto', 'posteriore'],
      ['foto', 'retro']
    ],
    initialKeys: ['foto_posteriore'],
    initialPaths: [
      ['foto_posteriore'],
      ['foto', 'posteriore'],
      ['foto', 'retro']
    ],
    isPrimary: true
  },
  {
    key: 'foto_lato_sinistro',
    label: 'Foto lato sinistro',
    answerKeys: ['foto_lato_sinistro', 'foto_laterale'],
    answerPaths: [
      ['foto_lato_sinistro'],
      ['foto', 'profiloSinistro'],
      ['foto', 'profiloSinistra'],
      ['foto', 'lateraleSinistro'],
      ['foto', 'lateraleSinistra']
    ],
    initialKeys: ['foto_lato_sinistro', 'foto_laterale_sinistra', 'foto_laterale'],
    initialPaths: [
      ['foto_lato_sinistro'],
      ['foto', 'profiloSinistro'],
      ['foto', 'profiloSinistra'],
      ['foto', 'lateraleSinistro'],
      ['foto', 'lateraleSinistra']
    ],
    isPrimary: false
  },
  {
    key: 'foto_lato_destro',
    label: 'Foto lato destro',
    answerKeys: ['foto_lato_destro', 'foto_extra'],
    answerPaths: [
      ['foto_lato_destro'],
      ['foto', 'profiloDestro'],
      ['foto', 'profiloDestra'],
      ['foto', 'lateraleDestro'],
      ['foto', 'lateraleDestra']
    ],
    initialKeys: ['foto_lato_destro', 'foto_laterale_destra', 'foto_extra'],
    initialPaths: [
      ['foto_lato_destro'],
      ['foto', 'profiloDestro'],
      ['foto', 'profiloDestra'],
      ['foto', 'lateraleDestro'],
      ['foto', 'lateraleDestra']
    ],
    isPrimary: false
  }
];

const manualPhotoPairs = [
  {
    key: 'front',
    label: 'Foto frontale',
    slots: [
      { field: 'imageOneUrl', label: 'Foto 1' },
      { field: 'imageTwoUrl', label: 'Foto 2' }
    ]
  },
  {
    key: 'back',
    label: 'Foto posteriore',
    slots: [
      { field: 'imageThreeUrl', label: 'Foto 3' },
      { field: 'imageFourUrl', label: 'Foto 4' }
    ]
  }
];

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (error) {
      return null;
    }
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

function formatMeasurement(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    const abs = Math.abs(value);
    const precision = abs >= 10 ? 1 : 2;
    const fixed = value.toFixed(precision);
    const asNumber = Number(fixed);
    if (Number.isInteger(asNumber)) return String(asNumber);
    return fixed.replace('.', ',');
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }
  return String(value);
}

const metaValueAtPath = (source, path = []) => {
  if (!source || typeof source !== 'object' || !Array.isArray(path) || !path.length) {
    return null;
  }
  let current = source;
  for (const segment of path) {
    if (current && typeof current === 'object' && segment in current) {
      current = current[segment];
    } else {
      return null;
    }
  }
  return current;
};

const normalizeMetaList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.flatMap(item => normalizeMetaList(item));
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value.files)) {
      return normalizeMetaList(value.files);
    }
    return [value];
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('gs://')) {
      return [{ url: trimmed }];
    }
    return [{ path: trimmed }];
  }
  return [];
};

const collectPhotoMetas = (answers, keys = [], paths = []) => {
  if (!answers || typeof answers !== 'object') return [];
  const metas = [];
  const pushValue = (raw) => {
    normalizeMetaList(raw).forEach(meta => {
      if (meta && typeof meta === 'object') metas.push(meta);
    });
  };

  keys.forEach((key) => {
    if (!key) return;
    const raw = answers[key];
    if (!raw) return;
    if (raw && typeof raw === 'object' && Array.isArray(raw.files)) {
      pushValue(raw.files);
    } else {
      pushValue(raw);
    }
  });

  paths.forEach((path) => {
    if (!Array.isArray(path) || !path.length) return;
    const raw = metaValueAtPath(answers, path);
    if (!raw) return;
    if (raw && typeof raw === 'object' && Array.isArray(raw.files)) {
      pushValue(raw.files);
    } else {
      pushValue(raw);
    }
  });

  return metas;
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function formatMeasurementValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    const str = Number.isInteger(value) ? String(value) : value.toFixed(2);
    return str.replace('.', ',');
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  return String(value);
}

function valueFromAnswer(answers, key) {
  if (!answers || !key || typeof answers !== 'object') return null;
  const raw = answers[key];
  if (raw === undefined || raw === null) return null;
  if (raw && typeof raw === 'object' && Array.isArray(raw.files)) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed.length ? trimmed : null;
  }
  return raw;
}

export default {
  name: 'UserCheckStorico',
  components: { UserAppChrome, UserSectionHeader },
  setup() {
    const router = useRouter();
    const db = getFirestore();
    const storage = getStorage();
    const auth = getAuth();

    const userId = ref('');
    const authLoading = ref(true);
    const checks = ref([]);
    const checksLoading = ref(false);
    const checksError = ref('');
    const questionnaireDocs = ref([]);
    const questionnaireComparisons = ref([]);
    const questionnaireLoading = ref(false);
    const questionnaireError = ref('');
    const percorsi = ref([]);
    const hideChecksFromUser = ref(false);
    const userVisibilityLoading = ref(false);
    const selectedImage = ref(null);
    const measurementAccordion = ref(new Set());

    const fileUrlCache = new Map();
    let unsubscribeAuth = null;
    let unsubscribeChecks = null;
    let unsubscribeQuestionari = null;
    let unsubscribePercorsi = null;
    let unsubscribeUser = null;

    const formatDateBasic = (dateInput) => {
      const d = toDate(dateInput);
      if (!d) return '';
      const dd = d.getDate().toString().padStart(2, '0');
      const mm = (d.getMonth() + 1).toString().padStart(2, '0');
      const yy = d.getFullYear();
      return `${dd}/${mm}/${yy}`;
    };

    const formatDate = (dateInput) => formatDateBasic(dateInput);
    const compareByDateDesc = (a, b) => {
      const aCreated = toDate(a?.createdAt)?.getTime() ?? -Infinity;
      const bCreated = toDate(b?.createdAt)?.getTime() ?? -Infinity;
      if (aCreated !== bCreated) return bCreated - aCreated;

      const aUpdated = toDate(a?.updatedAt)?.getTime() ?? -Infinity;
      const bUpdated = toDate(b?.updatedAt)?.getTime() ?? -Infinity;
      if (aUpdated !== bUpdated) return bUpdated - aUpdated;

      const aId = String(a?.id || '');
      const bId = String(b?.id || '');
      return bId.localeCompare(aId);
    };

    const formatDateTime = (dateInput) => {
      const d = toDate(dateInput);
      if (!d) return '';
      const datePart = d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const timePart = d.toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${datePart} ${timePart}`;
    };

    const openLightbox = (url) => {
      if (!url) return;
      selectedImage.value = url;
    };

    const closeLightbox = () => {
      selectedImage.value = null;
    };

    const goToTicketPage = () => {
      router.push('/ticket-page-tecnico');
    };

    const goBack = () => {
      router.back();
    };

    const handleLogout = async () => {
      try {
        await signOut(auth);
        router.push('/');
      } catch (error) {
        console.error('Errore durante il logout:', error);
      }
    };

    const visibleManualPhotoPairs = (check) => {
      if (!check) return [];
      return manualPhotoPairs.filter(pair =>
        pair.slots.some(slot => !!check[slot.field])
      );
    };

    const ensureUrls = async (metaList = []) => {
      const urls = [];
      for (const meta of metaList) {
        if (!meta) continue;

        if (typeof meta === 'string') {
          const trimmed = meta.trim();
          if (!trimmed) continue;
          if (/^https?:\/\//i.test(trimmed)) {
            urls.push(trimmed);
            continue;
          }
          if (!fileUrlCache.has(trimmed)) {
            try {
              const url = await getDownloadURL(storageRef(storage, trimmed));
              fileUrlCache.set(trimmed, url);
            } catch (err) {
              console.error('ensureUrls error', err);
              fileUrlCache.set(trimmed, null);
            }
          }
          const resolvedDirect = fileUrlCache.get(trimmed);
          if (resolvedDirect) urls.push(resolvedDirect);
          continue;
        }

        const directUrl =
          meta.url ||
          meta.downloadUrl ||
          meta.downloadURL ||
          meta._url;
        if (directUrl) {
          urls.push(directUrl);
          continue;
        }

        const path = meta.path || meta.storagePath || meta.referencePath;
        if (path) {
          if (!fileUrlCache.has(path)) {
            try {
              const url = await getDownloadURL(storageRef(storage, path));
              fileUrlCache.set(path, url);
            } catch (err) {
              console.error('ensureUrls error', err);
              fileUrlCache.set(path, null);
            }
          }
          const resolved = fileUrlCache.get(path);
          if (resolved) urls.push(resolved);
          continue;
        }

        if (Array.isArray(meta.files) && meta.files.length) {
          const nested = await ensureUrls(meta.files);
          urls.push(...nested);
        }
      }
      return Array.from(new Set(urls.filter(Boolean)));
    };

    const buildMeasurements = (answers, defs, dateLabel) => {
      if (!answers) return [];
      return defs
        .map(def => {
          const keys = [def.key, ...(def.aliases || [])];
          let raw = null;
          for (const key of keys) {
            raw = valueFromAnswer(answers, key);
            if (raw !== null && raw !== undefined && raw !== '') break;
          }
          const formatted = formatMeasurementValue(raw);
          if (!formatted) return null;
          return {
            caratteristica: def.label,
            valore: formatted,
            data: dateLabel || ''
          };
        })
        .filter(Boolean);
    };

    const buildPhotoComparisons = async ({ answers, initialAnswers, extraInitialAnswers = [], baselinePhotos }) => {
      const out = [];
      for (const field of PHOTO_FIELD_MAP) {
        const answerKeys = field.answerKeys || (field.key ? [field.key] : []);
        const initialKeys = Array.from(new Set([...(field.initialKeys || []), ...answerKeys]));
        const initialPaths = Array.from(
          new Map(
            [...(field.initialPaths || []), ...(field.answerPaths || [])]
              .map(path => [Array.isArray(path) ? path.join('.') : String(path), path])
          ).values()
        );
        const checkMetas = collectPhotoMetas(answers, answerKeys, field.answerPaths || []);
        const initialMetas = [
          ...collectPhotoMetas(initialAnswers, initialKeys, initialPaths),
          ...extraInitialAnswers.flatMap(source => collectPhotoMetas(source, initialKeys, initialPaths))
        ];

        let checkUrls = await ensureUrls(checkMetas);
        let initialUrls = await ensureUrls(initialMetas);

        if (baselinePhotos && baselinePhotos[field.key]?.length) {
          initialUrls = Array.from(new Set([...initialUrls, ...baselinePhotos[field.key]]));
        }

        if (!initialUrls.length && !checkUrls.length) continue;

        out.push({
          key: field.key,
          label: field.label,
          initialUrls,
          checkUrls,
          isPrimary: !!field.isPrimary
        });
      }
      return out;
    };

    const extractManualBaselinePhotos = (baseline) => {
      if (!baseline) return {};
      const photos = {};
      const push = (key, url) => {
        if (!url) return;
        if (!photos[key]) photos[key] = [];
        if (!photos[key].includes(url)) photos[key].push(url);
      };
      push('foto_frontale', baseline.imageOneUrl);
      push('foto_posteriore', baseline.imageTwoUrl);
      push('foto_lato_sinistro', baseline.imageThreeUrl);
      push('foto_lato_destro', baseline.imageFourUrl);
      if (Array.isArray(baseline.photoComparisons)) {
        baseline.photoComparisons.forEach(entry => {
          const key = entry?.key || entry?.label;
          if (!key) return;
          const normalizedKey = key.startsWith('foto_') ? key : `foto_${key}`;
          (Array.isArray(entry.initialUrls) ? entry.initialUrls : []).forEach(url => push(normalizedKey, url));
        });
      }
      return photos;
    };

    const buildComparisonFromQuestion = async (docData, initialDoc, manualBaseline, extraInitialAnswers = []) => {
      if (!docData) return null;
      const answers = docData.answers || {};
      const percorsoId = docData.percorsoId || initialDoc?.percorsoId || null;
      const stepIndexValue = toOptionalNumber(docData.stepIndex);
      const createdAt = toDate(docData.createdAt);

      const docMeta = {
        percorsoId,
        stepIndex: stepIndexValue,
        stepKey: docData.stepKey || 'check_finale',
        sequenceGroupId: docData.sequenceGroupId || null,
        sequenceIndex: toOptionalNumber(docData.sequenceIndex),
        sequenceTotal: toOptionalNumber(docData.sequenceTotal),
        productId: docData.productId || null,
        productName: docData.productName || null,
        programTitle: docData.programTitle || docData.programLabel || null,
        programLabel: docData.programLabel || docData.programTitle || null,
        plannedStartAt: docData.plannedStartAt || null,
        expireAt: docData.expireAt || null,
        stepName: docData.stepName || null
      };

      const snapshotInitialAnswers = docData.initialAnswersSnapshot || null;
      const initialAnswers = initialDoc?.answers || snapshotInitialAnswers;
      const snapshotInfo = docData.initialQuestionarioInfoSnapshot || {};
      const initialDateSource = initialDoc?.createdAt || snapshotInfo.createdAt || null;
      const initialDateLabel = initialDateSource ? formatDate(initialDateSource) : '';
      const checkDateLabel = createdAt ? formatDate(createdAt) : '';
      const baselinePhotos = manualBaseline ? extractManualBaselinePhotos(manualBaseline) : {};
      const allExtraInitialAnswers = [
        snapshotInitialAnswers && snapshotInitialAnswers !== initialAnswers ? snapshotInitialAnswers : null,
        ...extraInitialAnswers
      ].filter(Boolean);

      let measurementsOne = buildMeasurements(initialAnswers, INITIAL_MEASUREMENT_FIELDS, initialDateLabel);
      if ((!measurementsOne || !measurementsOne.length) && Array.isArray(manualBaseline?.measurementsOne) && manualBaseline.measurementsOne.length) {
        measurementsOne = manualBaseline.measurementsOne.map(row => ({ ...row }));
      }
      const measurementsTwo = buildMeasurements(answers, CHECK_MEASUREMENT_FIELDS, checkDateLabel);

      const photoComparisons = await buildPhotoComparisons({
        answers,
        initialAnswers,
        extraInitialAnswers: allExtraInitialAnswers,
        baselinePhotos
      });
      const primaryPhotoComparisons = photoComparisons.filter(entry => entry.isPrimary);
      const combinedPhotos = primaryPhotoComparisons.length ? primaryPhotoComparisons : photoComparisons;

      if (!combinedPhotos.length && !measurementsOne.length && !measurementsTwo.length) {
        return null;
      }

      const baselineCheckId =
        manualBaseline?.id ||
        (initialDoc?.source === 'converted_check' ? initialDoc.id : null);
      const baselineSource = manualBaseline
        ? 'manual_check'
        : initialDoc
        ? initialDoc.source || 'questionnaire'
        : null;

      const initialMeta = initialDoc
        ? {
            id: initialDoc.id,
            percorsoId: initialDoc.percorsoId || docMeta.percorsoId || null,
            stepIndex: toOptionalNumber(initialDoc.stepIndex),
            stepKey: initialDoc.stepKey || 'questionario',
            sequenceGroupId: initialDoc.sequenceGroupId || docMeta.sequenceGroupId || null,
            sequenceIndex: toOptionalNumber(initialDoc.sequenceIndex),
            sequenceTotal: toOptionalNumber(initialDoc.sequenceTotal),
            productId: initialDoc.productId || docMeta.productId || null,
            productName: initialDoc.productName || docMeta.productName || null,
            programTitle: initialDoc.programTitle || docMeta.programTitle || null,
            programLabel: initialDoc.programLabel || docMeta.programLabel || null,
            plannedStartAt: initialDoc.plannedStartAt || docMeta.plannedStartAt || null,
            expireAt: initialDoc.expireAt || docMeta.expireAt || null,
            stepName: initialDoc.stepName || null
          }
        : null;

      return {
        id: `questionario_${docData.id}`,
        creationMode: 'automatic',
        linkedQuestionnaireId: docData.id,
        initialQuestionnaireId: initialDoc?.id || null,
        baselineCheckId,
        baselineSource,
        percorsoId,
        stepIndex: stepIndexValue,
        stepKey: docMeta.stepKey,
        createdAt,
        photoComparisons: combinedPhotos,
        measurementsOne,
        measurementsTwo,
        meta: docMeta,
        initialMeta
      };
    };

    const mapCheckDoc = (docSnap) => {
      const data = docSnap.data() || {};
      return {
        id: docSnap.id,
        creationMode: data.creationMode || (data.linkedQuestionnaireId ? 'automatic' : 'manual'),
        linkedQuestionnaireId: data.linkedQuestionnaireId || null,
        initialQuestionnaireId: data.initialQuestionnaireId || null,
        percorsoId: data.percorsoId || null,
        stepIndex: toOptionalNumber(data.stepIndex),
        stepKey: data.stepKey || null,
        text: data.text || '',
        measurementsOne: Array.isArray(data.measurementsOne) ? data.measurementsOne : [],
        measurementsTwo: Array.isArray(data.measurementsTwo) ? data.measurementsTwo : [],
        imageOneUrl: data.imageOneUrl || '',
        imageTwoUrl: data.imageTwoUrl || '',
        imageThreeUrl: data.imageThreeUrl || '',
        imageFourUrl: data.imageFourUrl || '',
        photoComparisons: Array.isArray(data.photoComparisons) ? data.photoComparisons : [],
        visibilityProfile: data.visibilityProfile ?? true,
        approved: data.approved === true,
        approvedAt: toDate(data.approvedAt),
        gender: data.gender || '',
        ageGroup: data.ageGroup || '',
        menopausa: data.menopausa || '',
        haSeguito: data.haSeguito || '',
        tipoPercorso: data.tipoPercorso || '',
        haAvutoProgressi: data.haAvutoProgressi || '',
        respectAllenamento: data.respectAllenamento || '',
        respectNutrizione: data.respectNutrizione || '',
        acquaAssunta: data.acquaAssunta || '',
        riposoCorretta: data.riposoCorretta || '',
        isBaseline: data.isBaseline || data.creationMode === 'manual_baseline',
        autoGeneratedBaseline: data.autoGeneratedBaseline === true,
        createdAt: toDate(data.createdAt),
        updatedAt: toDate(data.updatedAt),
        sequenceGroupId: data.sequenceGroupId || null,
        sequenceIndex: toOptionalNumber(data.sequenceIndex),
        sequenceTotal: toOptionalNumber(data.sequenceTotal),
        productId: data.productId || null,
        productName: data.productName || null,
        programTitle: data.programTitle || data.programLabel || null,
        programLabel: data.programLabel || data.programTitle || null,
        plannedStartAt: data.plannedStartAt || null,
        expireAt: data.expireAt || null,
        stepName: data.stepName || null,
        meta: {
          percorsoId: data.percorsoId || null,
          stepIndex: toOptionalNumber(data.stepIndex),
          stepKey: data.stepKey || null,
          sequenceGroupId: data.sequenceGroupId || null,
          sequenceIndex: toOptionalNumber(data.sequenceIndex),
          sequenceTotal: toOptionalNumber(data.sequenceTotal),
          productId: data.productId || null,
          productName: data.productName || null,
          programTitle: data.programTitle || data.programLabel || null,
          programLabel: data.programLabel || data.programTitle || null,
          plannedStartAt: data.plannedStartAt || null,
          expireAt: data.expireAt || null,
          stepName: data.stepName || null
        },
        initialMeta: null
      };
    };

    const subscribeChecks = (uid) => {
      if (unsubscribeChecks) {
        unsubscribeChecks();
        unsubscribeChecks = null;
      }
      checks.value = [];
      checksError.value = '';
      checksLoading.value = false;
      if (!uid) return;

      checksLoading.value = true;
      try {
        const baseQuery = query(
          collection(db, 'check'),
          where('userId', '==', uid),
          orderBy('createdAt', 'desc')
        );
        unsubscribeChecks = onSnapshot(
          baseQuery,
          (snap) => {
            checks.value = snap.docs.map(mapCheckDoc);
            checksLoading.value = false;
            checksError.value = '';
          },
          (err) => {
            console.error('Errore caricamento check:', err);
            checks.value = [];
            checksLoading.value = false;
            checksError.value = err.message || String(err);
          }
        );
      } catch (err) {
        console.error('Errore creazione query check:', err);
        checks.value = [];
        checksLoading.value = false;
        checksError.value = err.message || String(err);
      }
    };

    const rebuildQuestionnaireComparisons = async () => {
      const rows = questionnaireDocs.value || [];
      if (!rows.length) {
        questionnaireComparisons.value = [];
        questionnaireLoading.value = false;
        return;
      }
      questionnaireLoading.value = true;
      try {
        const grouped = new Map();
        rows.forEach((row) => {
          const key = questionnaireGroupKey(row);
          if (!grouped.has(key)) grouped.set(key, []);
          grouped.get(key).push(row);
        });

        const comparisons = [];

        const toInitialDoc = (source, sourceType = 'questionnaire') => ({
          id: source.id,
          answers: source.answers || {},
          createdAt: toDate(source.createdAt),
          percorsoId: source.percorsoId || null,
          stepIndex: toOptionalNumber(source.stepIndex),
          stepKey: sourceType === 'converted_check' ? 'questionario' : source.stepKey || 'questionario',
          sequenceGroupId: source.sequenceGroupId || null,
          sequenceIndex: toOptionalNumber(source.sequenceIndex),
          sequenceTotal: toOptionalNumber(source.sequenceTotal),
          productId: source.productId || null,
          productName: source.productName || null,
          programTitle: source.programTitle || source.programLabel || null,
          programLabel: source.programLabel || source.programTitle || null,
          plannedStartAt: source.plannedStartAt || null,
          expireAt: source.expireAt || null,
          stepName: source.stepName || null,
          raw: source,
          source: sourceType
        });

        for (const list of grouped.values()) {
          const initialCandidates = list
            .filter(item => (item.stepKey || '').toLowerCase() === 'questionario')
            .sort((a, b) => {
              const ta = toDate(a.createdAt)?.getTime() ?? Number.POSITIVE_INFINITY;
              const tb = toDate(b.createdAt)?.getTime() ?? Number.POSITIVE_INFINITY;
              return ta - tb;
            });

          const convertedCandidates = list
            .filter(item => isConvertedBaselineDoc(item))
            .sort((a, b) => {
              const ta = toDate(a.createdAt)?.getTime() ?? Number.POSITIVE_INFINITY;
              const tb = toDate(b.createdAt)?.getTime() ?? Number.POSITIVE_INFINITY;
              return ta - tb;
            });

          const initialSource =
            initialCandidates.find((candidate) => hasBaselineData(candidate)) ||
            convertedCandidates.find((candidate) => hasBaselineData(candidate)) ||
            initialCandidates[0] ||
            convertedCandidates[0] ||
            null;
          const initialDoc = initialSource
            ? toInitialDoc(
                initialSource,
                isConvertedBaselineDoc(initialSource) ? 'converted_check' : 'questionnaire'
              )
            : null;
          const extraInitialAnswers = [initialCandidates[0], convertedCandidates[0]]
            .filter(source => source && source.id !== initialDoc?.id)
            .map(source => source.answers || {});

          const baselineDocId = initialDoc?.id || null;
          const checkRows = list
            .filter(item => (item.stepKey || '').toLowerCase() === 'check_finale')
            .filter(item => item.id !== baselineDocId);

          for (const row of checkRows) {
            const percorsoId = row.percorsoId || initialDoc?.percorsoId || null;
            const manualBaseline = percorsoId ? manualBaselinesByPercorso.value.get(percorsoId) : null;
            const comp = await buildComparisonFromQuestion(row, initialDoc, manualBaseline, extraInitialAnswers);
            if (comp) comparisons.push(comp);
          }
        }

        questionnaireComparisons.value = comparisons;
        questionnaireError.value = '';
      } catch (errInner) {
        console.error('Errore costruzione comparazioni questionari:', errInner);
        questionnaireComparisons.value = [];
        questionnaireError.value = errInner.message || String(errInner);
      } finally {
        questionnaireLoading.value = false;
      }
    };

    const subscribeQuestionari = (uid) => {
      if (unsubscribeQuestionari) {
        unsubscribeQuestionari();
        unsubscribeQuestionari = null;
      }
      questionnaireComparisons.value = [];
      questionnaireError.value = '';
      if (!uid) {
        questionnaireLoading.value = false;
        questionnaireDocs.value = [];
        questionnaireComparisons.value = [];
        return;
      }

      questionnaireDocs.value = [];

      try {
        const qRef = query(collection(db, 'questionari'), where('uid', '==', uid));
        questionnaireLoading.value = true;
        unsubscribeQuestionari = onSnapshot(
          qRef,
          async (snap) => {
            const rawRows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            questionnaireDocs.value = rawRows;
            await rebuildQuestionnaireComparisons();
          },
          (err) => {
            console.error('Errore caricamento questionari:', err);
            questionnaireComparisons.value = [];
            questionnaireLoading.value = false;
            questionnaireError.value = err.message || String(err);
          }
        );
      } catch (err) {
        console.error('Errore creazione query questionari:', err);
        questionnaireComparisons.value = [];
        questionnaireLoading.value = false;
        questionnaireError.value = err.message || String(err);
      }
    };

    const subscribePercorsi = (uid) => {
      if (unsubscribePercorsi) {
        unsubscribePercorsi();
        unsubscribePercorsi = null;
      }
      percorsi.value = [];
      if (!uid) return;
      try {
        const percorsiQuery = query(
          collection(db, 'percorsi'),
          where('uid', '==', uid)
        );
        unsubscribePercorsi = onSnapshot(
          percorsiQuery,
          (snap) => {
            percorsi.value = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          },
          (error) => {
            console.error('Errore caricando i percorsi:', error);
            percorsi.value = [];
          }
        );
      } catch (error) {
        console.error('Errore inizializzando i percorsi:', error);
        percorsi.value = [];
      }
    };

    const subscribeUserVisibility = (uid) => {
      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }
      hideChecksFromUser.value = false;
      userVisibilityLoading.value = false;
      if (!uid) return;
      userVisibilityLoading.value = true;
      try {
        const userRef = doc(db, 'users', uid);
        unsubscribeUser = onSnapshot(
          userRef,
          (snap) => {
            const data = snap.data() || {};
            hideChecksFromUser.value = data.hideChecksFromUser === true;
            userVisibilityLoading.value = false;
          },
          (error) => {
            console.error('Errore caricando la visibilita check utente:', error);
            hideChecksFromUser.value = false;
            userVisibilityLoading.value = false;
          }
        );
      } catch (error) {
        console.error('Errore inizializzando la visibilita check utente:', error);
        hideChecksFromUser.value = false;
        userVisibilityLoading.value = false;
      }
    };

    const manualHistoryChecks = computed(() => {
      const list = checks.value.filter((check) =>
        check.creationMode === 'manual' ||
        (check.creationMode === 'manual_baseline' && !check.autoGeneratedBaseline)
      );
      return list.sort(compareByDateDesc);
    });

    const automaticChecks = computed(() =>
      checks.value.filter(check => {
        const inferredAuto =
          check.creationMode === 'automatic' ||
          (!!check.linkedQuestionnaireId && check.creationMode !== 'manual');
        return inferredAuto;
      })
    );

    const manualBaselinesByPercorso = computed(() => {
      const map = new Map();
      checks.value.forEach(check => {
        if (!check?.percorsoId) return;
        const isBaseline = check.isBaseline || check.creationMode === 'manual_baseline';
        if (!isBaseline) return;
        const hasMeasurements = Array.isArray(check.measurementsOne) && check.measurementsOne.length;
        const hasPhotos = !!(check.imageOneUrl || check.imageTwoUrl || check.imageThreeUrl || check.imageFourUrl);
        const hasPhotoComparisons = Array.isArray(check.photoComparisons) && check.photoComparisons.length;
        if (!hasMeasurements && !hasPhotos && !hasPhotoComparisons) return;
        const createdTs = toDate(check.createdAt)?.getTime() ?? 0;
        const updatedTs = toDate(check.updatedAt)?.getTime() ?? createdTs;
        const existing = map.get(check.percorsoId);
        const existingCreated = existing ? existing.timestamp : -Infinity;
        const existingUpdated = existing ? (existing.updatedTimestamp ?? existingCreated) : -Infinity;
        const shouldReplace =
          !existing ||
          createdTs > existingCreated ||
          (createdTs === existingCreated && updatedTs >= existingUpdated);
        if (shouldReplace) {
          map.set(check.percorsoId, { ...check, timestamp: createdTs, updatedTimestamp: updatedTs });
        }
      });
      return map;
    });

    const convertedStepsByPercorso = computed(() => {
      const map = new Map();
      percorsi.value.forEach(percorso => {
        if (!percorso?.id) return;
        const steps = Array.isArray(percorso.steps) ? percorso.steps : [];
        const idx = steps.findIndex(step => step?.convertedFromQuestionario);
        if (idx < 0) return;
        map.set(percorso.id, { stepIndex: idx, step: steps[idx], percorso });
      });
      return map;
    });

    const isConvertedBaselineDoc = (doc) => {
      if (!doc) return false;
      const percorsoId = doc.percorsoId || null;
      if (!percorsoId) return false;
      const meta = convertedStepsByPercorso.value.get(percorsoId);
      if (!meta) return false;
      const docIndex = toOptionalNumber(doc.stepIndex);
      if (Number.isInteger(meta.stepIndex) && docIndex != null && meta.stepIndex !== docIndex) {
        return false;
      }
      const key = (doc.stepKey || '').toLowerCase();
      return key === 'check_finale';
    };

    const photoComparisonsFor = (check) => {
      if (Array.isArray(check.photoComparisons) && check.photoComparisons.length) {
        return check.photoComparisons.map(entry => ({
          key: entry.key || entry.label || '',
          label: entry.label || 'Foto',
          initialUrls: Array.isArray(entry.initialUrls) ? entry.initialUrls : [],
          checkUrls: Array.isArray(entry.checkUrls) ? entry.checkUrls : [],
          isPrimary: !!entry.isPrimary
        }));
      }
      const legacyPairs = [
        { key: 'manual_pair_1', label: 'Confronto Foto 1-2', initialUrl: check.imageOneUrl, checkUrl: check.imageTwoUrl },
        { key: 'manual_pair_2', label: 'Confronto Foto 3-4', initialUrl: check.imageThreeUrl, checkUrl: check.imageFourUrl }
      ]
        .map(pair => ({
          key: pair.key,
          label: pair.label,
          initialUrls: pair.initialUrl ? [pair.initialUrl] : [],
          checkUrls: pair.checkUrl ? [pair.checkUrl] : []
        }))
        .filter(entry => entry.initialUrls.length || entry.checkUrls.length);

      return legacyPairs;
    };

    const hasPhotoComparison = (check) => photoComparisonsFor(check).some(entry => entry.initialUrls.length || entry.checkUrls.length);

    const measurementComparisonsFor = (check) => {
      const map = new Map();
      const addRow = (row, type) => {
        if (!row || !row.caratteristica) return;
        const key = row.caratteristica;
        const entry = map.get(key) || {
          label: key,
          initialValue: '',
          checkValue: '',
          initialDate: '',
          checkDate: ''
        };
        if (type === 'initial') {
          entry.initialValue = row.valore ?? '';
          entry.initialDate = row.data ?? '';
        } else if (type === 'check') {
          entry.checkValue = row.valore ?? '';
          entry.checkDate = row.data ?? '';
        }
        map.set(key, entry);
      };

      (Array.isArray(check.measurementsOne) ? check.measurementsOne : []).forEach(row => addRow(row, 'initial'));
      (Array.isArray(check.measurementsTwo) ? check.measurementsTwo : []).forEach(row => addRow(row, 'check'));
      return Array.from(map.values());
    };

    const hasMeasurements = (check) => measurementComparisonsFor(check).length > 0;

    const normalizedAccordionKey = (checkId) => {
      if (checkId === null || checkId === undefined) return null;
      try {
        return String(checkId);
      } catch (error) {
        return null;
      }
    };

    const toggleMeasurements = (checkId) => {
      const key = normalizedAccordionKey(checkId);
      if (key === null) return;
      const next = new Set(measurementAccordion.value);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      measurementAccordion.value = next;
    };

    const isMeasurementsOpen = (checkId) => {
      const key = normalizedAccordionKey(checkId);
      if (key === null) return false;
      return measurementAccordion.value.has(key);
    };

    const automaticComparisonMap = computed(() => {
      const map = new Map();
      automaticChecks.value.forEach(check => {
        const key = check.linkedQuestionnaireId || check.id;
        map.set(key, check);
      });
      return map;
    });

    const comparisonChecks = computed(() => {
      const map = new Map(automaticComparisonMap.value);

      questionnaireComparisons.value.forEach(comp => {
        const key = comp.linkedQuestionnaireId || comp.id;
        const existing = map.get(key);
        if (!existing) {
          map.set(key, comp);
          return;
        }

        map.set(key, {
          ...existing,
          ...comp,
          id: existing.id || comp.id,
          creationMode: existing.creationMode || comp.creationMode,
          linkedQuestionnaireId: existing.linkedQuestionnaireId || comp.linkedQuestionnaireId,
          photoComparisons: Array.isArray(comp.photoComparisons) && comp.photoComparisons.length
            ? comp.photoComparisons
            : existing.photoComparisons,
          measurementsOne: Array.isArray(comp.measurementsOne) && comp.measurementsOne.length
            ? comp.measurementsOne
            : existing.measurementsOne,
          measurementsTwo: Array.isArray(comp.measurementsTwo) && comp.measurementsTwo.length
            ? comp.measurementsTwo
            : existing.measurementsTwo,
          meta: { ...(existing.meta || {}), ...(comp.meta || {}) },
          initialMeta: comp.initialMeta || existing.initialMeta || null
        });
      });

      const list = Array.from(map.values()).filter(entry => hasPhotoComparison(entry) || hasMeasurements(entry));
      return list.sort(compareByDateDesc);
    });

    const comparisonsLoading = computed(() => checksLoading.value || questionnaireLoading.value);
    const comparisonsError = computed(() => checksError.value || questionnaireError.value);
    const pageLoading = computed(
      () => authLoading.value || checksLoading.value || questionnaireLoading.value || userVisibilityLoading.value
    );

    watch(
      manualBaselinesByPercorso,
      () => {
        if (questionnaireDocs.value.length) {
          rebuildQuestionnaireComparisons();
        }
      },
      { deep: true }
    );

    watch(
      percorsi,
      () => {
        if (questionnaireDocs.value.length) {
          rebuildQuestionnaireComparisons();
        }
      },
      { deep: true }
    );

    watch(
      userId,
      (uid) => {
        subscribeChecks(uid || null);
        subscribeQuestionari(uid || null);
        subscribePercorsi(uid || null);
        subscribeUserVisibility(uid || null);
      },
      { immediate: true }
    );

    onMounted(() => {
      unsubscribeAuth = onAuthStateChanged(auth, (user) => {
        userId.value = user?.uid || '';
        authLoading.value = false;
      });
    });

    onBeforeUnmount(() => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeChecks) unsubscribeChecks();
      if (unsubscribeQuestionari) unsubscribeQuestionari();
      if (unsubscribePercorsi) unsubscribePercorsi();
      if (unsubscribeUser) unsubscribeUser();
    });

    return {
      userId,
      pageLoading,
      hideChecksFromUser,
      formatDate,
      formatDateTime,
      comparisonsLoading,
      comparisonsError,
      comparisonChecks,
      hasPhotoComparison,
      photoComparisonsFor,
      hasMeasurements,
      measurementComparisonsFor,
      formatMeasurement,
      checksLoading,
      checksError,
      manualHistoryChecks,
      visibleManualPhotoPairs,
      selectedImage,
      openLightbox,
      closeLightbox,
      goToTicketPage,
      goBack,
      handleLogout,
      toggleMeasurements,
      isMeasurementsOpen
    };
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:wght@700;900&display=swap');

.white-text {
  color: #ffffff;
}

.material-icons {
  color: #dedede;
}

.user-checks-body {
  color: #e5e5e5;
  display: grid;
  gap: 12px;
  padding: 0;
}

.loader,
.no-user {
  background: #1a1a1a;
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  color: #bdbdbd;
}

.hero-card {
  border-radius: 14px;
  background: #1a1a1a;
  border: 1px solid #232323;
  padding: 12px;
  display: grid;
  gap: 6px;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.75rem;
  color: #a4a4a4;
  margin: 0;
}

.eyebrow-light {
  color: #c1c1c1;
}

.hero-card h1 {
  margin: 0;
  font-size: 1.3rem;
  color: #ffffff;
}

.lead {
  font-size: 0.95rem;
  color: #e5e5e5;
  margin: 0;
}

.muted {
  color: #9a9a9a;
  margin: 0;
}

.error-text {
  color: #f87171;
  margin: 0;
}

.section-card {
  background: #111111;
  border-radius: 14px;
  padding: 12px;
  border: 1px solid #232323;
  display: grid;
  gap: 12px;
}

.section-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.section-header--outside {
  padding: 0 12px;
}

.section-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  color: #b6b6b6;
  margin: 0;
}

.section-header h2 {
  margin: 0;
  color: #ffffff;
  font-size: 1.1rem;
}

.comparison-list {
  display: grid;
  gap: 10px;
}

.comparison-card {
  background: #141414;
  border-radius: 12px;
  padding: 10px;
  border: 1px solid #262626;
  display: grid;
  gap: 10px;
}

.comparison-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.comparison-head h3 {
  margin: 0;
  color: #ffffff;
  font-size: 1rem;
}

.comparison-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #b3b3b3;
  font-size: 0.8rem;
}

.comparison-photos {
  display: grid;
  gap: 10px;
}

.photo-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(247, 208, 71, 0.12);
  border: 1px solid rgba(247, 208, 71, 0.35);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #f7d047;
}

.photo-comparison {
  background: #161616;
  border-radius: 12px;
  padding: 8px;
  border: 1px solid #262626;
  display: grid;
  gap: 8px;
}

.photo-comparison__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.photo-comparison__head h4 {
  margin: 0;
  font-size: 0.95rem;
  color: #ffffff;
}

.photo-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.photo-columns--single {
  grid-template-columns: minmax(0, 1fr);
}

.photo-col {
  background: #101010;
  border-radius: 12px;
  padding: 8px;
  text-align: center;
  border: 1px solid #262626;
  display: flex;
  flex-direction: column;
}

.photo-col-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #9a9a9a;
  text-transform: uppercase;
  font-size: 0.72rem;
}

.photo-roll {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-top: auto;
}

.photo-col-img {
  width: 100%;
  max-width: 140px;
  aspect-ratio: 2 / 3;
  object-fit: cover;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.photo-empty {
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.8rem;
  padding: 8px;
  border-radius: 10px;
  background: #1b1b1b;
  margin-top: auto;
}

.comparison-measurements {
  margin-top: 6px;
}

.measurement-toggle {
  width: 100%;
  border: 1px solid #262626;
  border-radius: 12px;
  background: #141414;
  color: inherit;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  cursor: pointer;
  text-align: left;
}

.measurement-toggle__title {
  margin: 0;
  font-weight: 600;
  color: #ffffff;
}

.measurement-toggle__hint {
  color: #9a9a9a;
  font-size: 0.72rem;
}

.measurement-toggle__icon {
  transition: transform 0.25s ease;
  align-self: flex-end;
}

.measurement-toggle__icon--open {
  transform: rotate(180deg);
}

.measurements-grid {
  display: grid;
  gap: 8px;
  margin-top: 8px;
}

.measurement-card {
  background: #141414;
  border-radius: 12px;
  padding: 10px;
  border: 1px solid #262626;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.measurement-label {
  margin: 0;
  font-weight: 600;
  color: #ffffff;
}

.measurement-values {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
}

.measurement-pill {
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: #1a1a1a;
  border: 1px solid #262626;
}

.measurement-pill--initial {
  background: rgba(247, 208, 71, 0.12);
  border-color: rgba(247, 208, 71, 0.35);
}

.measurement-pill--check {
  background: #1a1a1a;
  border-color: #2a2a2a;
}

.pill-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #b3b3b3;
}

.pill-value {
  font-size: 1.05rem;
  font-weight: 600;
}

.pill-date {
  font-size: 0.75rem;
  color: #9a9a9a;
}

.comparison-placeholder {
  color: #9a9a9a;
  text-align: center;
  background: #151515;
  border-radius: 12px;
  padding: 10px;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.check-card {
  background: #141414;
  border-radius: 12px;
  padding: 10px;
  border: 1px solid #262626;
}

.check-card header h3 {
  margin: 0 0 0.3rem;
  color: #ffffff;
}

.check-card header p {
  margin: 0;
  color: #9a9a9a;
  font-size: 0.82rem;
}

.check-text {
  white-space: pre-wrap;
  color: #bdbdbd;
  margin-top: 6px;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 6px;
  margin: 8px 0;
}

.photo-box {
  cursor: pointer;
  border-radius: 12px;
  overflow: hidden;
  background: #111111;
  text-align: center;
  border: 1px solid #262626;
}

.photo-box img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}

.photo-box small {
  display: block;
  padding: 4px 6px;
  color: #b3b3b3;
}

.tables-container {
  display: grid;
  gap: 10px;
}

.tables-container h4 {
  margin: 0 0 4px;
  color: #e5e5e5;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  background-color: #111111;
  border-radius: 12px;
  border: 1px solid #262626;
}

.table-wrapper--manual {
  background-color: #ffffff;
  border-color: #e5e7eb;
}

.table-wrapper table {
  width: 100%;
  border-collapse: collapse;
  color: #e5e7eb;
}

.table-wrapper--manual table {
  background-color: #ffffff;
  color: #111827;
}

.table-wrapper th,
.table-wrapper td {
  border: 1px solid #262626;
  padding: 6px;
  font-size: 0.85rem;
}

.table-wrapper--manual th,
.table-wrapper--manual td {
  border-color: #e5e7eb;
  color: #111827;
}

.ticket-button {
  display: block;
  margin: 8px auto 0;
  padding: 12px 16px;
  width: 100%;
  border: none;
  border-radius: 13px;
  background: linear-gradient(45deg, #f7d047, #f4b923);
  color: #050505;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.ticket-button:active {
  transform: translateY(1px);
}

.lightbox {
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.lightbox-content {
  max-width: 90%;
  max-height: 90%;
  border-radius: 12px;
}

.close {
  position: absolute;
  top: 20px;
  right: 20px;
  color: #f1f1f1;
  font-size: 32px;
  font-weight: bold;
  cursor: pointer;
}

.ucs-accordion-enter-active,
.ucs-accordion-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.ucs-accordion-enter-from,
.ucs-accordion-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.ucs-accordion-enter-to,
.ucs-accordion-leave-from {
  opacity: 1;
  transform: translateY(0);
}

@media (min-width: 640px) {
  .section-header {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
  .comparison-head {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .comparison-meta {
    align-items: flex-end;
    text-align: right;
  }
  .measurement-toggle {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .measurement-toggle__icon {
    align-self: auto;
  }
  .measurements-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }
  .measurement-values {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 768px) {
  .tables-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Fusco Fit user-area visual system */
.check-history-page {
  --ff-bg: #f5f4f0;
  --ff-white: #fff;
  --ff-black: #111110;
  --ff-muted: #66635f;
  --ff-red: #c8102e;
  --ff-red-dark: #990b22;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  color: var(--ff-black);
  background:
    radial-gradient(circle at 92% 8%, rgba(200, 16, 46, 0.1), transparent 25rem),
    radial-gradient(circle at 3% 74%, rgba(200, 16, 46, 0.05), transparent 28rem),
    var(--ff-bg);
  font-family: 'DM Sans', sans-serif;
  text-align: left;
}

.check-history-page,
.check-history-page *,
.check-history-page *::before,
.check-history-page *::after {
  box-sizing: border-box;
}

.check-history-page::before {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  content: '';
  opacity: 0.24;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.08'/%3E%3C/svg%3E");
}

.check-history-page .material-icons {
  color: currentColor;
}

.check-history-shell.user-checks-body {
  display: grid;
  width: min(100%, 1160px);
  gap: clamp(28px, 5vw, 48px);
  margin: 0 auto;
  padding: clamp(34px, 6vw, 72px) clamp(20px, 5vw, 52px) 148px;
  color: var(--ff-black);
}

.history-state {
  display: flex;
  min-height: 280px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 32px;
  color: var(--ff-muted);
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.68);
  box-shadow: 0 22px 50px rgba(47, 38, 32, 0.1);
  text-align: center;
  backdrop-filter: blur(20px) saturate(1.35);
  -webkit-backdrop-filter: blur(20px) saturate(1.35);
}

.history-state > .material-icons {
  color: var(--ff-red);
  font-size: 30px;
}

.history-state strong {
  color: var(--ff-black);
  font-size: 1rem;
}

.history-state p {
  margin: 0;
  font-size: 0.8rem;
}

.history-state__spinner {
  width: 31px;
  height: 31px;
  margin-bottom: 7px;
  border: 3px solid rgba(200, 16, 46, 0.12);
  border-top-color: var(--ff-red);
  border-radius: 50%;
  animation: check-history-spin 0.8s linear infinite;
}

@keyframes check-history-spin {
  to { transform: rotate(360deg); }
}

.check-history-page .section-header {
  display: flex;
  flex-direction: row;
  gap: 18px;
  align-items: flex-end;
  justify-content: space-between;
}

.check-history-page .section-header--outside {
  margin-bottom: -30px;
  padding: 0 7px;
}

.check-history-page .section-label {
  margin: 0 0 6px;
  color: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.check-history-page .section-header h2 {
  margin: 0;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1.65rem, 4vw, 2.45rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.055em;
  text-align: left;
  text-transform: uppercase;
}

.check-history-page .section-header > div {
  min-width: 0;
  text-align: left;
}

.check-history-page .muted {
  color: var(--ff-muted);
}

.check-history-page .section-header > small {
  max-width: 350px;
  font-size: 0.72rem;
  line-height: 1.4;
  text-align: right;
}

.check-history-page .section-card {
  display: grid;
  gap: 18px;
  padding: clamp(18px, 3vw, 26px);
  color: var(--ff-black);
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.68);
  box-shadow: 0 20px 48px rgba(47, 38, 32, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(20px) saturate(1.35);
  -webkit-backdrop-filter: blur(20px) saturate(1.35);
}

.check-history-page .section-card > p:not(.error-text),
.check-history-page .comparison-placeholder {
  margin: 0;
  padding: 24px;
  color: var(--ff-muted);
  border: 1px dashed rgba(17, 17, 16, 0.12);
  border-radius: 17px;
  background: rgba(245, 244, 240, 0.62);
  font-size: 0.82rem;
  text-align: center;
}

.check-history-page .error-text {
  margin: 0;
  padding: 14px;
  color: var(--ff-red-dark);
  border: 1px solid rgba(200, 16, 46, 0.12);
  border-radius: 14px;
  background: rgba(200, 16, 46, 0.06);
}

.check-history-page .comparison-list,
.check-history-page .history-list {
  display: grid;
  gap: 14px;
}

.check-history-page .comparison-card,
.check-history-page .check-card {
  position: relative;
  display: grid;
  gap: 15px;
  padding: clamp(16px, 3vw, 22px);
  overflow: hidden;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 12px 30px rgba(47, 38, 32, 0.07);
}

.check-history-page .comparison-card::before,
.check-history-page .check-card::before {
  position: absolute;
  top: 0;
  right: 22px;
  left: 22px;
  height: 2px;
  content: '';
  background: linear-gradient(90deg, transparent, var(--ff-red), transparent);
}

.check-history-page .comparison-head {
  display: flex;
  flex-direction: row;
  gap: 14px;
  align-items: flex-start;
  justify-content: space-between;
}

.check-history-page .comparison-head h3,
.check-history-page .check-card header h3 {
  margin: 0;
  color: var(--ff-black);
  font-size: 1.04rem;
  line-height: 1.25;
}

.check-history-page .comparison-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: var(--ff-muted);
  font-size: 0.7rem;
  text-align: right;
}

.check-history-page .photo-comparison {
  display: grid;
  gap: 10px;
  padding: 12px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 17px;
  background: rgba(245, 244, 240, 0.68);
}

.check-history-page .photo-comparison__head h4 {
  color: var(--ff-black);
  font-size: 0.84rem;
}

.check-history-page .photo-col {
  padding: 9px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.76);
}

.check-history-page .photo-col-title {
  color: var(--ff-muted);
  font-size: 0.6875rem;
  letter-spacing: 0.08em;
}

.check-history-page .photo-col-img {
  max-width: 210px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 13px;
  box-shadow: 0 10px 22px rgba(47, 38, 32, 0.1);
}

.check-history-page .photo-empty {
  color: var(--ff-muted);
  background: rgba(17, 17, 16, 0.035);
}

.check-history-page .measurement-toggle {
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  padding: 13px 15px;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.09);
  border-radius: 15px;
  background: rgba(245, 244, 240, 0.76);
  font: inherit;
}

.check-history-page .measurement-toggle__title,
.check-history-page .measurement-label {
  color: var(--ff-black);
}

.check-history-page .measurement-toggle__hint {
  color: var(--ff-muted);
}

.check-history-page .measurement-toggle__icon {
  align-self: auto;
  color: var(--ff-red);
}

.check-history-page .measurements-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.check-history-page .measurement-card {
  gap: 8px;
  padding: 12px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.72);
}

.check-history-page .measurement-values {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.check-history-page .measurement-pill {
  border-color: rgba(17, 17, 16, 0.08);
  background: rgba(245, 244, 240, 0.78);
}

.check-history-page .measurement-pill--initial {
  border-color: rgba(200, 16, 46, 0.12);
  background: rgba(200, 16, 46, 0.06);
}

.check-history-page .pill-label,
.check-history-page .pill-date {
  color: var(--ff-muted);
}

.check-history-page .pill-value {
  color: var(--ff-black);
}

.check-history-page .check-text {
  color: var(--ff-muted);
}

.check-history-page .tables-container h4 {
  color: var(--ff-black);
}

.check-history-page .table-wrapper,
.check-history-page .table-wrapper--manual {
  border-color: rgba(17, 17, 16, 0.09);
  background: rgba(255, 255, 255, 0.76);
}

.check-history-page .table-wrapper table,
.check-history-page .table-wrapper--manual table,
.check-history-page .table-wrapper th,
.check-history-page .table-wrapper td,
.check-history-page .table-wrapper--manual th,
.check-history-page .table-wrapper--manual td {
  color: var(--ff-black);
  border-color: rgba(17, 17, 16, 0.08);
  background: transparent;
}

.check-history-page .ticket-button {
  position: relative;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 34px;
  gap: 12px;
  align-items: center;
  width: 100%;
  margin: -6px 0 0;
  padding: 13px 15px;
  overflow: hidden;
  color: var(--ff-white);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 20px;
  background:
    radial-gradient(circle at 90% 0, rgba(200, 16, 46, 0.58), transparent 16rem),
    linear-gradient(140deg, #1c1c1a, #0e0e0d);
  box-shadow: 0 18px 40px rgba(17, 17, 16, 0.17);
  font: inherit;
  font-size: 0.83rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.check-history-page .ticket-button::after {
  position: absolute;
  right: 20px;
  bottom: 0;
  left: 20px;
  height: 3px;
  content: '';
  background: linear-gradient(90deg, var(--ff-red), #ff3157, transparent 88%);
}

.check-history-page .ticket-button .material-icons:first-child {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  font-size: 20px;
}

.check-history-page .ticket-button .material-icons:last-child {
  justify-self: end;
  font-size: 19px;
}

.check-history-page .lightbox {
  z-index: 2100;
  background: rgba(8, 8, 8, 0.92);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.check-history-page .lightbox-content {
  max-width: min(92%, 920px);
  max-height: 88%;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 18px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
}

.check-history-page .close {
  top: max(18px, env(safe-area-inset-top));
  right: 18px;
  display: grid;
  width: 42px;
  height: 42px;
  margin: 0;
  padding: 0;
  place-items: center;
  color: var(--ff-black);
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  font-size: 25px;
}

.check-history-page :is(button, img):focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.3);
  outline-offset: 3px;
}

@media (max-width: 719px) {
  .check-history-shell.user-checks-body {
    gap: 34px;
    padding: 28px 12px 138px;
  }

  .check-history-page .section-header,
  .check-history-page .comparison-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .check-history-page .section-header--outside {
    margin-bottom: -20px;
  }

  .check-history-page .section-header > small,
  .check-history-page .comparison-meta {
    align-items: flex-start;
    text-align: left;
  }

  .check-history-page .section-card {
    gap: 15px;
    padding: 15px;
    border-radius: 20px;
  }

  .check-history-page .measurements-grid,
  .check-history-page .measurement-values {
    grid-template-columns: minmax(0, 1fr);
  }

  .check-history-page .photo-columns {
    gap: 7px;
  }

  .check-history-page .photo-col {
    padding: 7px;
  }

  .check-history-page .ticket-button {
    margin-top: -8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .history-state__spinner {
    animation: none;
  }

  .measurement-toggle__icon,
  .ucs-accordion-enter-active,
  .ucs-accordion-leave-active {
    transition: none;
  }
}
</style>
